import {
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import Column from 'antd/lib/table/Column';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generateUuid } from '../shared/utils';
import {
  ScheduleFrequencies,
  ScheduleEntryStatuses,
  allReportScheduleTemplatesQuery,
  createReportScheduleMutation,
  deleteReportScheduleMutation,
  generateDefaultScheduleQuery,
  updateReportScheduleMutation,
} from './constants';

const { Paragraph, Text } = Typography;

function deriveScheduleCountsFromLoanTerm(loanTermMonths) {
  const months = Math.max(Number(loanTermMonths || 0), 0);
  const weeklyMonths = Math.min(months, 6);
  const monthlyMonths = Math.max(Math.min(months, 12) - 6, 0);
  const quarterlyMonths = Math.max(months - 12, 0);

  const numWeekly =
    months > 0 ? Math.max(Math.round((weeklyMonths * 52) / 12), 1) : 0;
  return {
    numWeekly,
    numMonthly: monthlyMonths,
    numQuarterly: quarterlyMonths > 0 ? Math.ceil(quarterlyMonths / 3) : 0,
  };
}

function ScheduleEntryRow({ entry, templates, onRemove, readOnly }) {
  const template = templates?.find((t) => t._id === entry.templateId);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '4px 0',
      }}
    >
      <Space>
        <Tag color={ScheduleFrequencies[entry.frequency]?.color || 'default'}>
          {ScheduleFrequencies[entry.frequency]?.label || entry.frequency}
        </Tag>
        <Text>{entry.label}</Text>
        <Text type="secondary">
          {entry.dueDate
            ? dayjs(entry.dueDate).format('MMM D, YYYY')
            : 'No date'}
        </Text>
        {template && <Text type="secondary">({template.name})</Text>}
      </Space>
      <Space>
        <Tag color={ScheduleEntryStatuses[entry.status]?.color || 'default'}>
          {ScheduleEntryStatuses[entry.status]?.label || entry.status}
        </Tag>
        {!readOnly && onRemove && (
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onRemove(entry.entryId)}
          />
        )}
      </Space>
    </div>
  );
}

function ReportScheduleEditor({
  proposalId,
  holisticProjectId,
  schedule,
  loanDurationMonths,
  onScheduleCreated,
  onScheduleUpdated,
  readOnly = false,
  isActiveOnCreate = false,
  renderEntryActions = null,
}) {
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showOneOffModal, setShowOneOffModal] = useState(false);
  const [generateForm] = Form.useForm();
  const [oneOffForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [localEntries, setLocalEntries] = useState([]);

  // Load templates
  const { data: templatesData } = useQuery(allReportScheduleTemplatesQuery, {
    variables: { first: 100, filters: {} },
    fetchPolicy: 'cache-and-network',
  });
  const templates = useMemo(() => {
    const edges = templatesData?.allReportScheduleTemplates?.edges || [];
    return edges.map((e) => e.node);
  }, [templatesData]);

  const weeklyTemplates = useMemo(
    () => templates.filter((t) => t.frequency === 'WEEKLY'),
    [templates],
  );
  const monthlyTemplates = useMemo(
    () => templates.filter((t) => t.frequency === 'MONTHLY'),
    [templates],
  );
  const quarterlyTemplates = useMemo(
    () => templates.filter((t) => t.frequency === 'QUARTERLY'),
    [templates],
  );

  useEffect(() => {
    if (schedule?.entries) {
      setLocalEntries(schedule.entries);
    }
  }, [schedule]);

  const [generateDefaultSchedule] = useLazyQuery(generateDefaultScheduleQuery, {
    fetchPolicy: 'no-cache',
  });

  const [createSchedule] = useMutation(createReportScheduleMutation);
  const [updateSchedule] = useMutation(updateReportScheduleMutation);
  const [deleteSchedule] = useMutation(deleteReportScheduleMutation);

  const normalizedLoanTermMonths = useMemo(() => {
    const parsed = Number(loanDurationMonths);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [loanDurationMonths]);

  const hasValidLoanTerm = normalizedLoanTermMonths > 0;

  const derivedCounts = useMemo(
    () =>
      hasValidLoanTerm
        ? deriveScheduleCountsFromLoanTerm(normalizedLoanTermMonths)
        : { numWeekly: 0, numMonthly: 0, numQuarterly: 0 },
    [hasValidLoanTerm, normalizedLoanTermMonths],
  );

  const isFutureDueDate = useCallback((dueDate) => {
    if (!dueDate) {
      return false;
    }
    return dayjs(dueDate).isAfter(dayjs());
  }, []);

  const getEntryLockReason = useCallback(
    (entry) => {
      if (!entry) {
        return 'This entry cannot be modified.';
      }
      if (entry.scheduledReportId) {
        return 'This entry has already been started and is locked.';
      }

      const status = `${entry.status || ''}`.toUpperCase();
      if (status !== 'UPCOMING') {
        return 'Only upcoming entries can be modified.';
      }

      if (!isFutureDueDate(entry.dueDate)) {
        return 'Past-due entries cannot be modified.';
      }

      return null;
    },
    [isFutureDueDate],
  );

  const disableNonFutureDates = useCallback(
    (current) => current && current < dayjs().endOf('day'),
    [],
  );

  const handleGenerate = useCallback(
    async (values) => {
      if (!hasValidLoanTerm) {
        Modal.warning({
          title: 'Loan duration is required',
          content:
            'Enter a loan duration on the proposal, then try regenerating the report schedule preview.',
          okText: 'OK',
        });
        return;
      }

      setSaving(true);
      try {
        const result = await generateDefaultSchedule({
          variables: {
            input: {
              startDate: values.startDate?.toISOString(),
              weeklyTemplateId: values.weeklyTemplateId || null,
              monthlyTemplateId: values.monthlyTemplateId || null,
              quarterlyTemplateId: values.quarterlyTemplateId || null,
              numWeekly: derivedCounts.numWeekly,
              numMonthly: derivedCounts.numMonthly,
              numQuarterly: derivedCounts.numQuarterly,
            },
          },
        });
        const entries = result?.data?.generateDefaultSchedule?.entries || [];

        if (!entries.length) {
          notification.error({
            message: 'Generation failed',
            description:
              'No preview entries were generated. Existing schedule preview was left unchanged.',
          });
          return;
        }

        if (schedule?._id) {
          // Update existing schedule
          await updateSchedule({
            variables: {
              schedule: {
                _id: schedule._id,
                entries: entries.map((e) => ({
                  entryId: e.entryId,
                  templateId: e.templateId,
                  dueDate: e.dueDate,
                  frequency: e.frequency,
                  status: e.status,
                  label: e.label,
                })),
                defaultWeeklyTemplateId: values.weeklyTemplateId || null,
                defaultMonthlyTemplateId: values.monthlyTemplateId || null,
                defaultQuarterlyTemplateId: values.quarterlyTemplateId || null,
              },
            },
          });
          notification.success({
            message: 'Schedule Updated',
            description: 'Report schedule has been regenerated',
          });
          if (onScheduleUpdated) onScheduleUpdated();
        } else {
          // Create new schedule
          const resp = await createSchedule({
            variables: {
              schedule: {
                _id: generateUuid(),
                proposalId: proposalId,
                holisticProjectId: holisticProjectId || null,
                entries: entries.map((e) => ({
                  entryId: e.entryId,
                  templateId: e.templateId,
                  dueDate: e.dueDate,
                  frequency: e.frequency,
                  status: e.status,
                  label: e.label,
                })),
                defaultWeeklyTemplateId: values.weeklyTemplateId || null,
                defaultMonthlyTemplateId: values.monthlyTemplateId || null,
                defaultQuarterlyTemplateId: values.quarterlyTemplateId || null,
                isActive: isActiveOnCreate,
              },
            },
          });
          notification.success({
            message: 'Schedule Created',
            description: 'Report schedule has been generated',
          });
          if (onScheduleCreated) {
            onScheduleCreated(resp?.data?.createReportSchedule?.schedule);
          }
        }
        setShowGenerateModal(false);
      } catch (err) {
        console.error(err);
        notification.error({
          message: 'Error',
          description: 'Failed to generate schedule',
        });
      }
      setSaving(false);
    },
    [
      schedule,
      holisticProjectId,
      proposalId,
      generateDefaultSchedule,
      createSchedule,
      updateSchedule,
      onScheduleCreated,
      onScheduleUpdated,
      normalizedLoanTermMonths,
      hasValidLoanTerm,
      isActiveOnCreate,
    ],
  );

  const handleRemoveEntry = useCallback((entryId) => {
    setLocalEntries((prev) => prev.filter((e) => e.entryId !== entryId));
  }, []);

  const handleUpdateEntryDueDate = useCallback((entryId, dueDate) => {
    if (!dueDate) {
      return;
    }
    setLocalEntries((prev) =>
      prev.map((entry) =>
        entry.entryId === entryId
          ? {
              ...entry,
              dueDate: dueDate.toISOString(),
            }
          : entry,
      ),
    );
  }, []);

  const handleOpenOneOffModal = useCallback(() => {
    oneOffForm.resetFields();
    setShowOneOffModal(true);
  }, [oneOffForm]);

  const handleAddOneOff = useCallback(
    (values) => {
      const dueDate = values?.dueDate;
      if (!dueDate || !dayjs(dueDate).isAfter(dayjs())) {
        notification.error({
          message: 'Invalid due date',
          description: 'One-off reports must have a future due date.',
        });
        return;
      }

      const newEntry = {
        entryId: generateUuid(),
        templateId: values.templateId || null,
        dueDate: dueDate.toISOString(),
        frequency: 'CUSTOM',
        status: 'UPCOMING',
        scheduledReportId: null,
        label:
          values.label || `One-Off ${dayjs(dueDate).format('MMM D, YYYY')}`,
      };

      setLocalEntries((prev) =>
        [...prev, newEntry].sort(
          (a, b) => dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf(),
        ),
      );
      setShowOneOffModal(false);
      oneOffForm.resetFields();
    },
    [oneOffForm],
  );

  const handleSaveEntries = useCallback(async () => {
    if (!schedule?._id) return;
    setSaving(true);
    try {
      await updateSchedule({
        variables: {
          schedule: {
            _id: schedule._id,
            entries: localEntries.map((e) => ({
              entryId: e.entryId,
              templateId: e.templateId,
              dueDate: e.dueDate,
              frequency: e.frequency,
              status: e.status,
              label: e.label,
              scheduledReportId: e.scheduledReportId,
            })),
          },
        },
      });
      notification.success({
        message: 'Saved',
        description: 'Schedule entries updated successfully',
      });
      if (onScheduleUpdated) onScheduleUpdated();
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Error',
        description: 'Failed to save schedule entries',
      });
    }
    setSaving(false);
  }, [schedule, localEntries, updateSchedule, onScheduleUpdated]);

  const handleDeleteSchedule = useCallback(async () => {
    if (!schedule?._id) return;
    setSaving(true);
    try {
      await deleteSchedule({ variables: { _id: schedule._id } });
      notification.success({
        message: 'Deleted',
        description: 'Report schedule deleted',
      });
      if (onScheduleUpdated) onScheduleUpdated();
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Error',
        description: 'Failed to delete schedule',
      });
    }
    setSaving(false);
  }, [schedule, deleteSchedule, onScheduleUpdated]);

  const entriesByFrequency = useMemo(() => {
    const groups = { WEEKLY: [], MONTHLY: [], QUARTERLY: [], CUSTOM: [] };
    (localEntries || []).forEach((e) => {
      if (groups[e.frequency]) {
        groups[e.frequency].push(e);
      } else {
        groups.CUSTOM.push(e);
      }
    });
    return groups;
  }, [localEntries]);

  const summaryText = useMemo(() => {
    const parts = [];
    if (entriesByFrequency.WEEKLY.length)
      parts.push(`${entriesByFrequency.WEEKLY.length} weekly`);
    if (entriesByFrequency.MONTHLY.length)
      parts.push(`${entriesByFrequency.MONTHLY.length} monthly`);
    if (entriesByFrequency.QUARTERLY.length)
      parts.push(`${entriesByFrequency.QUARTERLY.length} quarterly`);
    if (entriesByFrequency.CUSTOM.length)
      parts.push(`${entriesByFrequency.CUSTOM.length} custom`);
    return parts.length > 0
      ? parts.join(', ') + ' reports'
      : 'No reports scheduled';
  }, [entriesByFrequency]);

  if (!schedule && readOnly) {
    return (
      <Card>
        <Empty description="No report schedule has been configured for this proposal." />
      </Card>
    );
  }

  return (
    <div>
      <Card
        title={
          <Space>
            <CalendarOutlined />
            <span>Report Schedule</span>
            {schedule?.isActive && <Tag color="green">Active</Tag>}
          </Space>
        }
        extra={
          !readOnly && (
            <Space>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setShowGenerateModal(true)}
              >
                {schedule ? 'Regenerate Schedule' : 'Generate Schedule'}
              </Button>
              {schedule && (
                <Button icon={<PlusOutlined />} onClick={handleOpenOneOffModal}>
                  Add One-Off Report
                </Button>
              )}
              {schedule && localEntries !== schedule.entries && (
                <Button
                  type="primary"
                  onClick={handleSaveEntries}
                  loading={saving}
                >
                  Save Changes
                </Button>
              )}
            </Space>
          )
        }
      >
        {!schedule && !readOnly && (
          <Empty description="No schedule yet. Click 'Generate Schedule' to create a default report schedule.">
            <Button
              type="primary"
              icon={<CalendarOutlined />}
              onClick={() => setShowGenerateModal(true)}
            >
              Generate Default Schedule
            </Button>
          </Empty>
        )}

        {(schedule || localEntries.length > 0) && (
          <>
            <Alert
              message={summaryText}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            {!readOnly && (
              <Alert
                message="Schedule governance rules"
                description="You can add entries at any time with a future due date. You can edit due date or delete only entries that are upcoming, have a future due date, and have not been started or submitted."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <Table
              dataSource={localEntries}
              rowKey="entryId"
              size="small"
              pagination={{ pageSize: 15, showSizeChanger: true }}
            >
              <Column title="Label" dataIndex="label" key="label" width={150} />
              <Column
                title="Frequency"
                dataIndex="frequency"
                key="frequency"
                width={120}
                render={(frequency) => (
                  <Tag
                    color={ScheduleFrequencies[frequency]?.color || 'default'}
                  >
                    {ScheduleFrequencies[frequency]?.label || frequency}
                  </Tag>
                )}
              />
              <Column
                title="Due Date"
                dataIndex="dueDate"
                key="dueDate"
                width={210}
                render={(date, record) => {
                  if (readOnly) {
                    return date ? dayjs(date).format('MMM D, YYYY') : '-';
                  }

                  const lockReason = getEntryLockReason(record);
                  if (lockReason) {
                    return (
                      <Tooltip title={lockReason}>
                        <Space size={4}>
                          <Text>
                            {date ? dayjs(date).format('MMM D, YYYY') : '-'}
                          </Text>
                          <LockOutlined style={{ color: '#999' }} />
                        </Space>
                      </Tooltip>
                    );
                  }

                  return (
                    <DatePicker
                      value={date ? dayjs(date) : null}
                      onChange={(nextDate) =>
                        handleUpdateEntryDueDate(record.entryId, nextDate)
                      }
                      disabledDate={disableNonFutureDates}
                      allowClear={false}
                    />
                  );
                }}
              />
              <Column
                title="Template"
                dataIndex="templateId"
                key="templateId"
                render={(templateId) => {
                  const tmpl = templates.find((t) => t._id === templateId);
                  return tmpl ? tmpl.name : <Text type="secondary">None</Text>;
                }}
              />
              <Column
                title="Status"
                dataIndex="status"
                key="status"
                width={110}
                render={(status) => (
                  <Tag
                    color={ScheduleEntryStatuses[status]?.color || 'default'}
                  >
                    {ScheduleEntryStatuses[status]?.label || status}
                  </Tag>
                )}
              />
              {renderEntryActions && (
                <Column
                  title=""
                  key="report-actions"
                  width={60}
                  render={(_, record) => renderEntryActions(record)}
                />
              )}
              {!readOnly && (
                <Column
                  title="Actions"
                  key="actions"
                  width={110}
                  render={(_, record) => {
                    const lockReason = getEntryLockReason(record);

                    if (lockReason) {
                      return (
                        <Tooltip title={lockReason}>
                          <Space>
                            <Button
                              type="text"
                              size="small"
                              disabled
                              icon={<EditOutlined />}
                            />
                            <Button
                              type="text"
                              size="small"
                              danger
                              disabled
                              icon={<DeleteOutlined />}
                            />
                          </Space>
                        </Tooltip>
                      );
                    }

                    return (
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveEntry(record.entryId)}
                      />
                    );
                  }}
                />
              )}
            </Table>
          </>
        )}
      </Card>

      <Modal
        title="Generate Report Schedule"
        open={showGenerateModal}
        onCancel={() => setShowGenerateModal(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Paragraph>
          Report duration is automatically derived from the repayment term. The
          schedule will use weekly reports through month 6, monthly through
          month 12, then quarterly through the end of the loan term.
        </Paragraph>
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Generated dates can be adjusted later"
          description="You can add, edit, or delete only upcoming future-due entries after generation. Past-due, started, and submitted entries are locked."
        />
        <Form
          form={generateForm}
          layout="vertical"
          onFinish={handleGenerate}
          initialValues={{ startDate: dayjs() }}
        >
          <Form.Item
            name="startDate"
            label="Preview Start Date"
            rules={[
              { required: true, message: 'Please select a preview start date' },
            ]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          {hasValidLoanTerm ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`Estimated preview from repayment term ${normalizedLoanTermMonths} months: ${derivedCounts.numWeekly} weekly, ${derivedCounts.numMonthly} monthly, ${derivedCounts.numQuarterly} quarterly reports`}
            />
          ) : (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Loan duration is required"
              description="Enter a loan duration on the proposal and click Generate again."
            />
          )}
          <Divider orientation="left">Templates</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="weeklyTemplateId" label="Weekly Template">
                <Select allowClear placeholder="Select template">
                  {weeklyTemplates.map((t) => (
                    <Select.Option key={t._id} value={t._id}>
                      {t.name}
                    </Select.Option>
                  ))}
                  {weeklyTemplates.length === 0 && (
                    <Select.Option disabled>No weekly templates</Select.Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="monthlyTemplateId" label="Monthly Template">
                <Select allowClear placeholder="Select template">
                  {monthlyTemplates.map((t) => (
                    <Select.Option key={t._id} value={t._id}>
                      {t.name}
                    </Select.Option>
                  ))}
                  {monthlyTemplates.length === 0 && (
                    <Select.Option disabled>No monthly templates</Select.Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="quarterlyTemplateId" label="Quarterly Template">
                <Select allowClear placeholder="Select template">
                  {quarterlyTemplates.map((t) => (
                    <Select.Option key={t._id} value={t._id}>
                      {t.name}
                    </Select.Option>
                  ))}
                  {quarterlyTemplates.length === 0 && (
                    <Select.Option disabled>
                      No quarterly templates
                    </Select.Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setShowGenerateModal(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                Generate
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
      <Modal
        title="Add One-Off Report"
        open={showOneOffModal}
        onCancel={() => setShowOneOffModal(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={oneOffForm} layout="vertical" onFinish={handleAddOneOff}>
          <Form.Item
            name="dueDate"
            label="Due Date"
            rules={[{ required: true, message: 'Please select a due date' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              disabledDate={disableNonFutureDates}
            />
          </Form.Item>
          <Form.Item name="templateId" label="Template">
            <Select allowClear placeholder="Select template">
              {templates.map((template) => (
                <Select.Option key={template._id} value={template._id}>
                  {template.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="label" label="Label (optional)">
            <Text
              type="secondary"
              style={{ display: 'block', marginBottom: 8 }}
            >
              If left blank, a label will be auto-generated.
            </Text>
            <Input placeholder="e.g. Special mid-year report" />
          </Form.Item>
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setShowOneOffModal(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Add One-Off Report
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
      <style jsx>{``}</style>
    </div>
  );
}

export default ReportScheduleEditor;
